import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { alunoEstaNaTurma } from '../utils/statusAluno';
import { useTheme } from '../contexts/ThemeContext';
import { fetchAllPaginated, fetchSchoolsSimple } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';

// Imagem do selo TEA (fita de conscientização do autismo)
const TEA_BADGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='teaGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23FF6B6B'/%3E%3Cstop offset='25%25' style='stop-color:%23FFE66D'/%3E%3Cstop offset='50%25' style='stop-color:%234ECDC4'/%3E%3Cstop offset='75%25' style='stop-color:%2345B7D1'/%3E%3Cstop offset='100%25' style='stop-color:%23A855F7'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M32 4c-2 0-4 1-5 3l-8 16c-1 2-3 3-5 3H8c-2 0-4 2-4 4v4c0 2 2 4 4 4h6c2 0 4 1 5 3l8 16c1 2 3 3 5 3s4-1 5-3l8-16c1-2 3-3 5-3h6c2 0 4-2 4-4v-4c0-2-2-4-4-4h-6c-2 0-4-1-5-3L37 7c-1-2-3-3-5-3z' fill='url(%23teaGrad)' stroke='%23fff' stroke-width='2'/%3E%3Ccircle cx='32' cy='32' r='8' fill='%23fff'/%3E%3Cpath d='M28 30h8M32 26v8' stroke='url(%23teaGrad)' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E";

function AlunosTEA() {
  const navigate = useNavigate();
  const { getUserCity, user } = useAuth();
  const isProfessor = user?.role === ROLES.PROFESSOR;
  const { darkMode } = useTheme();
  const userCity = getUserCity();

  // Estados principais
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [filtroEscola, setFiltroEscola] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroGrauTEA, setFiltroGrauTEA] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Opções de grau do AEE
  const teaGrauOptions = [
    { value: '', label: 'Todos os Graus' },
    { value: 'nivel_1', label: 'Nível 1 - Leve', color: 'green', description: 'Requer suporte' },
    { value: 'nivel_2', label: 'Nível 2 - Moderado', color: 'yellow', description: 'Requer suporte substancial' },
    { value: 'nivel_3', label: 'Nível 3 - Severo', color: 'red', description: 'Requer suporte muito substancial' },
  ];

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Restringe as buscas à cidade em foco (antes a tela baixava os dados
      // de todos os municípios do sistema).
      const cityId = userCity?.id || null;
      const cityParams = cityId ? { city: cityId } : {};
      const [alunosData, turmasData, escolasData, profilesData] = await Promise.all([
        fetchAllPaginated('/students/', cityParams),
        fetchAllPaginated('/classes/', cityParams),
        fetchSchoolsSimple(cityId),
        fetchAllPaginated('/student-profiles/', cityParams),
      ]);

      // Criar mapa de perfis por id_student
      const profilesMap = {};
      profilesData.forEach(p => {
        profilesMap[p.id_student] = p;
      });

      // Filtrar alunos com qualquer deficiência ou TEA
      // Professor só vê quem ainda está nas turmas dele (ver Alunos.jsx).
      const alunosComTEA = alunosData
        .filter(aluno => !isProfessor || alunoEstaNaTurma(aluno))
        .map(aluno => ({
          ...aluno,
          profile: profilesMap[aluno.id_student] || null,
          has_tea: profilesMap[aluno.id_student]?.has_tea || false,
          tea_grau: profilesMap[aluno.id_student]?.tea_grau || '',
          tem_deficiencia: profilesMap[aluno.id_student]?.tem_deficiencia || false,
          tipo_deficiencia: profilesMap[aluno.id_student]?.tipo_deficiencia || '',
        }))
        .filter(aluno => aluno.has_tea || aluno.tea_grau || aluno.tem_deficiencia);

      setAlunos(alunosComTEA);
      setTurmas(turmasData);
      setEscolas(escolasData);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [userCity, isProfessor]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Obter turmas filtradas por escola
  const turmasFiltradas = filtroEscola
    ? turmas.filter(t => t.id_school === parseInt(filtroEscola))
    : turmas;

  // Filtrar alunos
  const alunosFiltrados = alunos.filter(aluno => {
    const turmaAluno = turmas.find(t => t.id === aluno.id_class);

    const matchEscola = !filtroEscola || (turmaAluno && turmaAluno.id_school === parseInt(filtroEscola));
    const matchTurma = !filtroTurma || aluno.id_class === parseInt(filtroTurma);
    const matchGrauTEA = !filtroGrauTEA || aluno.tea_grau === filtroGrauTEA;
    const matchSearch = !searchTerm ||
      aluno.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      aluno.student_serial?.toString().includes(searchTerm);

    return matchEscola && matchTurma && matchGrauTEA && matchSearch;
  });

  // Estatísticas por grau
  const estatisticasPorGrau = useMemo(() => {
    return {
      total: alunos.length,
      nivel_1: alunos.filter(a => a.tea_grau === 'nivel_1').length,
      nivel_2: alunos.filter(a => a.tea_grau === 'nivel_2').length,
      nivel_3: alunos.filter(a => a.tea_grau === 'nivel_3').length,
      sem_grau: alunos.filter(a => !a.tea_grau && a.has_tea).length,
    };
  }, [alunos]);

  // Abrir perfil do aluno em página completa
  const handleViewProfile = (aluno) => {
    navigate(`/alunos/${aluno.id_student}`);
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltroEscola('');
    setFiltroTurma('');
    setFiltroGrauTEA('');
    setSearchTerm('');
  };

  // Obter informações da turma
  const getTurmaInfo = (idClass) => {
    const turma = turmas.find(t => t.id === idClass);
    return turma ? {
      turma: turma.class_name,
      escola: turma.school_name,
      grade: turma.grade,
      shift: turma.shift
    } : { turma: '-', escola: '-', grade: '-', shift: '-' };
  };

  // Obter cor do grau TEA
  const getGrauColor = (grau) => {
    switch (grau) {
      case 'nivel_1': return 'green';
      case 'nivel_2': return 'yellow';
      case 'nivel_3': return 'red';
      default: return 'purple';
    }
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Alunos AEE..."
        subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-red-50 border border-red-200'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error}</p>
          <button
            onClick={fetchData}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-red-100 text-[#E83A3C] hover:bg-red-200'
              }`}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div className="flex items-center gap-4">
          <img src={TEA_BADGE} alt="AEE" className="w-12 h-12" />
          <div>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-[#2E4974] to-[#0974BA]'
              : 'bg-gradient-to-r from-[#2E4974] to-[#0974BA]'
              }`}>
              Alunos AEE
            </h1>
            <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
              Acompanhamento especializado de alunos com Atendimento Educacional Especializado
              {userCity && <span className="text-[#2E4974]"> - {userCity.name}</span>}
            </p>
          </div>
        </div>
      </header>

      {/* Cards de Estatísticas por Grau */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="flex justify-center mb-2">
            <img src={TEA_BADGE} alt="AEE" className="w-8 h-8" />
          </div>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#2E4974] to-[#0974BA]">
            {estatisticasPorGrau.total}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total AEE</div>
        </div>

        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-[#13983D]/10 border border-[#13983D]/20'
          : 'bg-[#13983D]/5 border border-[#13983D]/20'
          }`}>
          <div className="text-2xl font-bold text-[#13983D]">
            {estatisticasPorGrau.nivel_1}
          </div>
          <div className={`text-xs ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>Nível 1 - Leve</div>
          <div className={`text-[10px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Requer suporte</div>
        </div>

        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-[#F9B047]/10 border border-[#F9B047]/20'
          : 'bg-yellow-50 border border-yellow-200'
          }`}>
          <div className="text-2xl font-bold text-[#F9B047]">
            {estatisticasPorGrau.nivel_2}
          </div>
          <div className={`text-xs ${darkMode ? 'text-[#F9B047]' : 'text-[#F9B047]'}`}>Nível 2 - Moderado</div>
          <div className={`text-[10px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Suporte substancial</div>
        </div>

        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20'
          : 'bg-red-50 border border-red-200'
          }`}>
          <div className="text-2xl font-bold text-[#E83A3C]">
            {estatisticasPorGrau.nivel_3}
          </div>
          <div className={`text-xs ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>Nível 3 - Severo</div>
          <div className={`text-[10px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Suporte intensivo</div>
        </div>

        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-400 to-slate-500">
            {estatisticasPorGrau.sem_grau}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sem grau definido</div>
          <div className={`text-[10px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Atualizar cadastro</div>
        </div>
      </div>

      {/* Informação sobre os níveis do AEE */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-[#2E4974]/10 border border-[#2E4974]/20'
        : 'bg-[#2E4974]/5 border border-[#2E4974]/20'
        }`}>
        <div className="flex items-start gap-3">
          <span className="text-xl">ℹ️</span>
          <div>
            <h3 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-[#2E4974]' : 'text-[#2E4974]'}`}>
              Níveis do AEE conforme DSM-5
            </h3>
            <p className={`text-xs ${darkMode ? 'text-[#2E4974]/80' : 'text-[#2E4974]/80'}`}>
              <strong>Nível 1:</strong> Dificuldades na comunicação social, inflexibilidade comportamental.
              <strong className="ml-2">Nível 2:</strong> Déficits marcantes, comportamentos restritos frequentes.
              <strong className="ml-2">Nível 3:</strong> Déficits graves, comportamentos muito inflexíveis, extrema dificuldade com mudanças.
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Busca */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Buscar por nome ou matrícula..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#2E4974]/50`}
            />
          </div>

          {/* Filtro por Escola */}
          <SearchableSelect
            value={filtroEscola}
            onChange={(v) => { setFiltroEscola(v); setFiltroTurma(''); }}
            options={escolas.map(escola => ({ value: escola.id, label: escola.school }))}
            emptyOptionLabel="Todas as escolas"
            darkMode={darkMode}
            className="w-full min-w-0"
          />

          {/* Filtro por Turma */}
          <SearchableSelect
            value={filtroTurma}
            onChange={setFiltroTurma}
            options={turmasFiltradas.map(turma => ({
              value: turma.id,
              label: `${turma.class_name}${turma.school_name ? ` (${turma.school_name})` : ''}`,
            }))}
            emptyOptionLabel="Todas as turmas"
            darkMode={darkMode}
            className="w-full min-w-0"
          />

          {/* Filtro por Grau AEE */}
          <SearchableSelect
            value={filtroGrauTEA}
            onChange={setFiltroGrauTEA}
            options={teaGrauOptions}
            placeholder="Selecione"
            darkMode={darkMode}
            className="w-full min-w-0"
          />
        </div>

        {/* Limpar filtros */}
        {(filtroEscola || filtroTurma || filtroGrauTEA || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className={`text-sm ${darkMode ? 'text-[#2E4974] hover:text-[#2E4974]/80' : 'text-[#2E4974] hover:text-[#2E4974]/80'}`}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Lista de Alunos */}
      {alunosFiltrados.length === 0 ? (
        <div className={`rounded-xl p-12 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <img src={TEA_BADGE} alt="AEE" className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Nenhum aluno encontrado
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {alunos.length === 0
              ? 'Não há alunos AEE cadastrados ainda.'
              : 'Nenhum aluno corresponde aos filtros aplicados.'}
          </p>
        </div>
      ) : (
        <div className={`rounded-xl overflow-hidden ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                  <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Aluno
                  </th>
                  <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Turma / Escola
                  </th>
                  <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Deficiência
                  </th>
                  <th className={`text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Grau AEE
                  </th>
                  <th className={`text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                {alunosFiltrados.map(aluno => {
                  const turmaInfo = getTurmaInfo(aluno.id_class);
                  const grauColor = getGrauColor(aluno.tea_grau);

                  return (
                    <tr
                      key={aluno.id_student}
                      className={`transition-colors cursor-pointer ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                      onClick={() => handleViewProfile(aluno)}
                    >
                      <td className={`px-4 py-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-base font-medium ${darkMode ? 'bg-gradient-to-br from-[#2E4974]/20 to-[#0974BA]/20 text-[#2E4974]' : 'bg-gradient-to-br from-[#2E4974]/10 to-[#0974BA]/10 text-[#2E4974]'}`}>
                            {aluno.student_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium truncate" title={aluno.student_name}>{aluno.student_name}</span>
                              {(aluno.has_tea || aluno.tea_grau) && (
                                <img src={TEA_BADGE} alt="AEE" className="w-4 h-4 shrink-0" />
                              )}
                            </div>
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {aluno.student_serial}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <div className="max-w-[220px]">
                          <div className="text-sm truncate" title={turmaInfo.turma}>{turmaInfo.turma}</div>
                          <div className={`text-xs truncate ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} title={turmaInfo.escola}>
                            {turmaInfo.escola}
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <span className="text-xs block max-w-[180px] truncate" title={aluno.tipo_deficiencia || (aluno.has_tea ? 'TEA' : '-')}>
                          {aluno.tipo_deficiencia || (aluno.has_tea ? 'TEA' : '-')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${grauColor === 'green' ? 'bg-[#13983D]/20 text-[#13983D]' :
                          grauColor === 'yellow' ? 'bg-[#F9B047]/20 text-[#F9B047]' :
                            grauColor === 'red' ? 'bg-[#E83A3C]/20 text-[#E83A3C]' :
                              'bg-[#2E4974]/20 text-[#2E4974]'
                          }`}>
                          {aluno.tea_grau === 'nivel_1' ? 'Nível 1' :
                            aluno.tea_grau === 'nivel_2' ? 'Nível 2' :
                              aluno.tea_grau === 'nivel_3' ? 'Nível 3' : 'Não definido'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleViewProfile(aluno)}
                          className={`p-2 rounded-lg transition-colors ${darkMode
                            ? 'hover:bg-[#2E4974]/20 text-slate-400 hover:text-[#2E4974]'
                            : 'hover:bg-[#2E4974]/5 text-slate-500 hover:text-[#2E4974]'
                            }`}
                          title="Ver Perfil"
                        >
                          👤
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default AlunosTEA;
